import { type ComponentPropsWithRef } from 'react'
import type React from 'react'
import type { TokenPath } from '../theme/tokens/generated/types'

export const variants = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body1', 'body2', 'body3', 'code'] as const
export type Variant = (typeof variants)[number]

/**
 * Semantic foreground colours for Typography, mapped to `theme.tokens.foreground.*`.
 * Values from Figma foreground tokens:
 * https://www.figma.com/design/enSt0PjzoMuMpMp8VmzFBe/%F0%9F%8E%A8Foundation?node-id=7-412&view=variables&var-set-id=3206-118545&m=dev
 *
 * Keeping a small named set makes usage simple (e.g. `color="primary"`) and type-safe —
 * an unknown name is a type error. Colours resolve at render time so light/dark mode works.
 */
export const typographyColorTokenPaths = {
  // foreground / main
  primary: 'foreground.main.primary',
  secondary: 'foreground.main.secondary',
  disabled: 'foreground.main.disabled',
  inverse: 'foreground.main.inverse',
  link: 'foreground.main.link',
  interactive: 'foreground.main.interactive',
  'static-white': 'foreground.main.staticWhite',
  // foreground / semantic
  neutral: 'foreground.semantic.neutral.default',
  neutralInverse: 'foreground.semantic.neutral.inverse',
  info: 'foreground.semantic.info.default',
  infoInverse: 'foreground.semantic.info.inverse',
  success: 'foreground.semantic.success.default',
  successInverse: 'foreground.semantic.success.inverse',
  warning: 'foreground.semantic.warning.default',
  warningInverse: 'foreground.semantic.warning.inverse',
  error: 'foreground.semantic.error.default',
  errorInverse: 'foreground.semantic.error.inverse'
} as const satisfies Record<string, TokenPath>

export type TypographyColor = keyof typeof typographyColorTokenPaths

export interface TypographyProps extends ComponentPropsWithRef<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> {
  /**
   * What is the variant of Typography
   */
  variant?: Variant
  /**
   * Semantic foreground colour, e.g. `'primary'`, `'secondary'`, `'error'`.
   * One of the named tokens in {@link typographyColorTokenPaths}.
   */
  color?: TypographyColor
  /**
   * Any component that will be rendered within Typography
   */
  children: React.ReactNode
  /**
   * Custom props to override the defaults
   */
  props?: any
}
